import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, extractAxiosError } from "../lib/api";
import { CurrencyAmount } from "../components/CurrencyAmount";
import { SecurityBadge } from "../components/SecurityBadge";
import { BankAccountDialog } from "../components/BankAccountDialog";
import { SplitWheel } from "../components/SplitWheel";
import { Recipients } from "../components/Recipients";
import { wheelMembers } from "../lib/poolMembers";
import { EmptyStateCard, PageShell } from "../components/EmptyStateWrapper";
import { BetaModeNotice } from "../components/BetaBadge";
import { LoadingIndicator } from "../components/ShellContentFrame";
import { RecipientConnectSetup } from "../components/RecipientConnectSetup";
import { DEFAULT_CURRENCY } from "../lib/defaultCurrency";
import { formatBankMask } from "../lib/bankingLocale";
import { Landmark, Lock, CheckCircle2, AlertCircle, Calendar, TrendingUp, ArrowDownRight, ShieldCheck, RefreshCw } from "lucide-react";

function formatDate(iso, opts = {}) {
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", ...opts }); }
    catch (_e) { return iso; }
}

function wheelMembersFromPool(members = []) {
    return wheelMembers(members).map((m) => ({
        id: m.id,
        name: m.name,
        role_label: m.role_label || "",
        percentage: m.percentage,
        color: m.color,
        status: m.status ?? "active",
        avatar_url: m.avatar_url,
        bank: m.bank,
    }));
}

async function enrichPoolMembers(pools = []) {
    return Promise.all(pools.map(async (row) => {
        if ((row.members || []).length > 0) return row;
        try {
            const { data } = await api.get(`/pools/${row.pool_id}`);
            return { ...row, members: wheelMembersFromPool(data.pool?.members || []) };
        } catch (_e) {
            return row;
        }
    }));
}

export function MemberDashboard() {
    const { activeOrg, memberships, user } = useAuth();
    const location = useLocation();
    const welcomedPoolName = location.state?.welcomedPoolName;
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [bankFor, setBankFor] = useState(null);
    const [poolForBank, setPoolForBank] = useState(null);
    const [pendingInviteCount, setPendingInviteCount] = useState(0);
    const [selectedPoolId, setSelectedPoolId] = useState(null);
    const [highlightId, setHighlightId] = useState(null);

    useEffect(() => {
        if ((memberships || []).length > 0) {
            setPendingInviteCount(0);
            return undefined;
        }
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.get("/invitations/inbox/count");
                if (mounted) setPendingInviteCount(data.count || 0);
            } catch (_e) {
                if (mounted) setPendingInviteCount(0);
            }
        })();
        return () => { mounted = false; };
    }, [memberships]);

    const load = async () => {
        if (!activeOrg?.id) return;
        setLoading(true);
        setLoadError("");
        try {
            const { data } = await api.get("/me/recipient-summary", { params: { org_id: activeOrg.id } });
            const pools = await enrichPoolMembers(data.pools || []);
            setSummary({ ...data, pools });
        } catch (e) {
            setSummary(null);
            setLoadError(extractAxiosError(e, "Could not load your dashboard"));
        } finally { setLoading(false); }
    };
    useEffect(() => {
        if (!activeOrg?.id) {
            setLoading(false);
            return;
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeOrg?.id]);

    useEffect(() => {
        const pools = summary?.pools || [];
        if (!pools.length) {
            setSelectedPoolId(null);
            setHighlightId(null);
            return;
        }
        setSelectedPoolId((current) => (
            current && pools.some((p) => p.pool_id === current) ? current : pools[0].pool_id
        ));
        setHighlightId((current) => current || pools[0].member?.id || null);
    }, [summary]);

    const memberLoading = loading || !activeOrg?.id;
    const showNoWorkspace = !(memberships || []).length;
    const poolCount = (summary?.pools || []).length;
    const showEmpty = !memberLoading && activeOrg?.id && !loadError && (!summary || poolCount === 0);
    const showContent = !memberLoading && activeOrg?.id && summary && poolCount > 0;
    const totalEarned = summary?.total_earned ?? 0;
    const totalUpcoming = (summary?.pools || []).reduce((s, row) => {
        const nextTotal = row.next_payout_total || 0;
        const pct = row.member?.percentage || 0;
        return s + (nextTotal * pct) / 100;
    }, 0);
    const banksReady = (summary?.pools || []).filter((r) => r.member?.bank?.account_last4).length;
    const firstName = (user?.name || "there").split(" ")[0];
    const focusPool = (summary?.pools || []).find((p) => p.pool_id === selectedPoolId) || summary?.pools?.[0] || null;
    const primaryCurrency = focusPool?.pool_currency || summary?.pools?.[0]?.pool_currency || DEFAULT_CURRENCY;
    const wheelHighlightId = highlightId || focusPool?.member?.id || null;
    const emptyTitle = welcomedPoolName
        ? `Welcome to ${welcomedPoolName}`
        : `Welcome to ${activeOrg?.name || "your workspace"}`;
    const emptyDescription = welcomedPoolName
        ? "Your pool membership is being set up. Refresh in a moment, or contact the pool owner if this persists."
        : "You're in this workspace. When an admin adds you to a revenue pool, your share and earnings will appear here.";

    return (
        <PageShell flow>
            <BetaModeNotice className="mb-6" />
            <div className="relative min-h-[28rem]" data-testid="member-dashboard-frame">
                <div className={memberLoading ? "pointer-events-none invisible" : ""} aria-hidden={memberLoading}>
                {showNoWorkspace && (
                    <EmptyStateCard flow testId="member-no-workspace">
                        <ShieldCheck className="mx-auto mb-3 h-7 w-7 text-[#00BF0D]/70" />
                        {pendingInviteCount > 0 ? (
                            <>
                                <p className="font-heading text-xl">You have a pending invitation</p>
                                <p className="mt-1 text-white/50">Accept it to join a workspace and see your pool shares here.</p>
                                <Link to="/invites" className="btn-neon mt-5 inline-flex rounded-md px-4 py-2 text-sm">Open Invite Center</Link>
                            </>
                        ) : (
                            <>
                                <p className="font-heading text-xl">No workspace yet</p>
                                <p className="mt-1 text-white/50">You&apos;re signed in, but you&apos;re not in a workspace. Accept an invite or ask an admin to add you.</p>
                                <Link to="/invites" className="btn-neon mt-5 inline-flex rounded-md px-4 py-2 text-sm">Check invites</Link>
                            </>
                        )}
                    </EmptyStateCard>
                )}

                {!showNoWorkspace && loadError && (
                    <EmptyStateCard flow testId="member-error">
                        <AlertCircle className="mx-auto mb-3 h-7 w-7 text-rose-400/80" />
                        <p className="font-heading text-xl">Couldn&apos;t load your dashboard</p>
                        <p className="mt-1 text-white/50">{loadError}</p>
                        <button type="button" onClick={load} className="btn-neon mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm">
                            <RefreshCw className="h-4 w-4" /> Try again
                        </button>
                    </EmptyStateCard>
                )}

                {!showNoWorkspace && showEmpty && (
                    <EmptyStateCard flow testId="member-empty">
                        <ShieldCheck className="mx-auto mb-3 h-7 w-7 text-[#00BF0D]/70" />
                        <p className="font-heading text-xl">{emptyTitle}</p>
                        <p className="mt-1 text-white/50">{emptyDescription}</p>
                        {welcomedPoolName && (
                            <button type="button" onClick={load} className="btn-ghost mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm">
                                <RefreshCw className="h-4 w-4" /> Refresh
                            </button>
                        )}
                    </EmptyStateCard>
                )}

                {!showNoWorkspace && showContent && summary && (
                    <div className="space-y-8" data-testid="member-content-pane">
                        <div className="flex flex-wrap items-end justify-between gap-3">
                            <div>
                                <p className="label-eyebrow">Your recipient view</p>
                                <h1 className="mt-1 font-heading text-3xl tracking-tight sm:text-4xl">Hi {firstName}.</h1>
                                <p className="mt-1.5 text-white/50">
                                    {poolCount === 1
                                        ? <>Your share in <span className="text-white">{summary.pools[0].pool_name}</span>.</>
                                        : <>Your shares and earnings in <span className="text-white">{activeOrg?.name || "your workspace"}</span>.</>}
                                </p>
                            </div>
                            <SecurityBadge />
                        </div>

                        <RecipientConnectSetup />

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div className="surface p-5" data-testid="member-card-earned">
                                <div className="flex items-center justify-between">
                                    <p className="label-eyebrow">Total earned</p>
                                    <TrendingUp className="h-3.5 w-3.5 text-[#00BF0D]/70" />
                                </div>
                                <p className="mt-2 font-heading font-mono text-3xl tracking-tight text-[#00BF0D] sm:text-4xl">
                                    <CurrencyAmount amount={totalEarned} currency={primaryCurrency} decimals={2} />
                                </p>
                                <p className="mt-1.5 text-xs text-white/40">{summary.earnings.length} payout{summary.earnings.length === 1 ? "" : "s"}</p>
                            </div>
                            <div className="surface p-5" data-testid="member-card-upcoming">
                                <div className="flex items-center justify-between">
                                    <p className="label-eyebrow">Your next disbursement</p>
                                    <Calendar className="h-3.5 w-3.5 text-white/40" />
                                </div>
                                <p className="mt-2 font-heading font-mono text-3xl tracking-tight text-white sm:text-4xl">
                                    <CurrencyAmount amount={totalUpcoming} currency={primaryCurrency} decimals={2} />
                                </p>
                                <p className="mt-1.5 text-xs text-white/45">Est. based on current allocations</p>
                            </div>
                            <div className="surface p-5" data-testid="member-card-bank-status">
                                <div className="flex items-center justify-between">
                                    <p className="label-eyebrow">Bank readiness</p>
                                    <Landmark className="h-3.5 w-3.5 text-white/40" />
                                </div>
                                <p className="mt-2 font-heading font-mono text-3xl tracking-tight text-white sm:text-4xl">
                                    {banksReady}/{(summary.pools || []).length}
                                </p>
                                <p className="mt-1.5 text-xs text-white/45">{banksReady === (summary.pools || []).length ? "All pools ready" : "Linking required"}</p>
                            </div>
                        </div>

                        {focusPool && (focusPool.members || []).length > 0 && (
                            <section className="grid gap-6 lg:grid-cols-3" data-testid="member-split-wheel-section">
                                <div className="relative col-span-2 flex min-h-[24rem] flex-col surface p-6 pb-8 lg:min-h-[32rem] lg:p-8">
                                    <div className="pointer-events-none absolute inset-0" aria-hidden style={{ background: "radial-gradient(ellipse at center, rgba(0,191,13,0.07), transparent 70%)" }} />
                                    <div className="relative mb-4 flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <p className="label-eyebrow">Allocations</p>
                                            <h2 className="mt-1 font-heading text-xl">The Split Wheel</h2>
                                            {poolCount > 1 && (
                                                <select
                                                    value={focusPool.pool_id}
                                                    onChange={(e) => setSelectedPoolId(e.target.value)}
                                                    className="input-dark mt-3 rounded-md px-2.5 py-1.5 text-xs"
                                                    data-testid="member-pool-select"
                                                >
                                                    {(summary.pools || []).map((p) => (
                                                        <option key={p.pool_id} value={p.pool_id}>{p.pool_name}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        <p className="text-xs text-white/45">{focusPool.pool_name}</p>
                                    </div>
                                    <div className="relative grid flex-1 place-items-center py-4">
                                        <SplitWheel
                                            members={focusPool.members || []}
                                            size={poolCount > 1 ? 340 : 400}
                                            stroke={11}
                                            highlightId={wheelHighlightId}
                                            onHover={setHighlightId}
                                            ariaLabel={`${focusPool.pool_name} allocation`}
                                        />
                                    </div>
                                </div>
                                <div className="col-span-1 flex min-h-[24rem] min-w-0 flex-col surface p-5 lg:min-h-[32rem]">
                                    <div className="mb-4 flex shrink-0 items-end justify-between">
                                        <div>
                                            <p className="label-eyebrow">Pool members</p>
                                            <h3 className="mt-1 font-heading text-lg">Who gets what</h3>
                                        </div>
                                        <span className="text-xs text-white/40">{wheelMembers(focusPool.members || []).length} active</span>
                                    </div>
                                    <Recipients
                                        scrollable
                                        pool={{ id: focusPool.pool_id, members: focusPool.members || [] }}
                                        members={focusPool.members || []}
                                        highlightId={wheelHighlightId}
                                        onHover={setHighlightId}
                                        poolId={focusPool.pool_id}
                                    />
                                </div>
                            </section>
                        )}

                        <section>
                            <h2 className="label-eyebrow mb-3">Your pools</h2>
                            <ul className="space-y-2.5">
                                {(summary.pools || []).map((row) => {
                                    const m = row.member;
                                    const hasBank = !!m.bank?.account_last4;
                                    return (
                                        <li key={row.pool_id} className="surface p-4" data-testid={`member-pool-${row.pool_id}`}>
                                            <div className="flex items-center gap-3">
                                                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: m.color }} />
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate font-medium text-white">{row.pool_name}</p>
                                                    <p className="text-xs capitalize text-white/45">{row.cadence} · next {row.next_payout_date ? formatDate(row.next_payout_date) : "—"}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-heading font-mono text-xl tabular-nums" style={{ color: m.color }}>
                                                        {m.percentage}%
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between gap-3">
                                                {hasBank ? (
                                                    <span className="inline-flex items-center gap-1.5 font-mono text-xs text-white/65">
                                                        <CheckCircle2 className="h-3.5 w-3.5 text-[#00BF0D]" />
                                                        <Lock className="h-3 w-3 text-white/40" />
                                                        <span>{formatBankMask(m) || `${m.bank.bank_name || "Bank"} ····${m.bank.account_last4}`}</span>
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-300/95">
                                                        <AlertCircle className="h-3.5 w-3.5" />
                                                        Bank account required to receive payouts
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => { setPoolForBank({ id: row.pool_id }); setBankFor(m); }}
                                                    className="btn-ghost inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs"
                                                    data-testid={`member-set-bank-${row.pool_id}`}
                                                >
                                                    <Landmark className="h-3 w-3" /> {hasBank ? "Update bank" : "Add bank"}
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>

                        <section>
                            <div className="mb-3 flex items-end justify-between">
                                <h2 className="label-eyebrow">Recent disbursements to you</h2>
                                <p className="font-mono text-xs text-white/45">{summary.earnings.length} entries</p>
                            </div>
                            {summary.earnings.length === 0 ? (
                                <div className="surface p-8 text-center text-white/45" data-testid="member-earnings-empty">
                                    <ArrowDownRight className="mx-auto mb-2 h-5 w-5 text-white/35" />
                                    Your first disbursement will appear here.
                                </div>
                            ) : (
                                <div className="surface overflow-hidden">
                                    <ul className="divide-y divide-white/[0.04]">
                                        {summary.earnings.map((e) => (
                                            <li key={e.payout_id} className="flex items-center justify-between px-4 py-3 sm:px-5" data-testid={`member-earning-${e.payout_id}`}>
                                                <div className="min-w-0">
                                                    <p className="text-sm text-white/85">{formatDate(e.date, { year: "numeric" })}</p>
                                                    <p className="font-mono text-[11px] text-white/45">{e.txn_id?.slice(0, 8)}…{e.txn_id?.slice(-4)} · {e.method?.toUpperCase()}{e.bank_last4 ? ` · ····${e.bank_last4}` : ""}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-mono font-semibold tabular-nums text-[#00BF0D]">
                                                        <CurrencyAmount amount={e.amount} currency={e.currency || primaryCurrency} decimals={2} inline hideConvert />
                                                    </p>
                                                    <span className="fs-pill shrink-0 gap-1 rounded-full border border-[#00BF0D]/20 bg-[#00BF0D]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#00BF0D]">
                                                        <CheckCircle2 className="h-3 w-3" /> {e.status}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </section>

                        <BankAccountDialog pool={poolForBank} member={bankFor} open={!!bankFor && !!poolForBank} onClose={() => { setBankFor(null); setPoolForBank(null); }} onSaved={() => load()} />
                    </div>
                )}
                </div>
                {memberLoading && !showNoWorkspace && (
                    <div className="absolute inset-0 z-10 grid place-items-center" data-testid="member-dashboard-loading">
                        <LoadingIndicator label="Loading your dashboard…" />
                    </div>
                )}
            </div>
        </PageShell>
    );
}
